import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { addDays, format, fromUnixTime, getUnixTime } from 'date-fns';

import { BehaviorSubject } from 'rxjs';
import { utcDifferenceInDays } from 'server/helpers/utcDifferenceInDays';
import {
  EmploymentAvailability,
  EmploymentAvailabilityStatusEnum,
  PoolerUser,
} from 'server/shared/models/user';
import { dbDayDateToken } from 'server/static/dbDayDateToken';
export interface EmploymentAvailabilityWithDaysBetween
  extends EmploymentAvailability {
  daysBetween: number;
  offset: number;
}

@Component({
  selector: 'employment-mini-availability-match',
  templateUrl: 'employment-mini-availability-match.component.html',
  styleUrls: ['employment-mini-availability-match.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmploymentMiniAvailabilityMatch implements OnInit, OnDestroy {
  private userInput$ = new BehaviorSubject<PoolerUser | undefined>(undefined);
  user$ = this.userInput$.asObservable();

  @Input() set user(user: PoolerUser | undefined) {
    this.userInput$.next(user);
  }
  get user() {
    return this.userInput$.value;
  }

  private externalAvailabilitiesInput$ = new BehaviorSubject<
    EmploymentAvailability[] | undefined
  >(undefined);
  externalAvailabilities$ = this.externalAvailabilitiesInput$.asObservable();

  @Input() set externalAvailabilities(
    externalAvailabilities: EmploymentAvailability[] | undefined,
  ) {
    this.externalAvailabilitiesInput$.next(externalAvailabilities);
  }
  get externalAvailabilities() {
    return this.externalAvailabilitiesInput$.value;
  }

  private fromDateInput$ = new BehaviorSubject<Date | undefined>(undefined);
  fromDate$ = this.fromDateInput$.asObservable();

  @Input() set fromDate(fromDate: Date | undefined) {
    this.fromDateInput$.next(fromDate);
  }
  get fromDate() {
    return this.fromDateInput$.value;
  }

  private toDateInput$ = new BehaviorSubject<Date | undefined>(undefined);
  toDate$ = this.toDateInput$.asObservable();

  @Input() set toDate(toDate: Date | undefined) {
    this.toDateInput$.next(toDate);
  }
  get toDate() {
    return this.toDateInput$.value;
  }

  private availabilityPercentageInput$ = new BehaviorSubject<number>(0);
  availabilityPercentage$ = this.availabilityPercentageInput$.asObservable();

  @Input() set availabilityPercentage(availabilityPercentage: number) {
    this.availabilityPercentageInput$.next(availabilityPercentage);
  }
  get availabilityPercentage() {
    return this.availabilityPercentageInput$.value;
  }

  statusEnum = EmploymentAvailabilityStatusEnum;
  availabilityDays$ = new BehaviorSubject<string[]>([]);
  employmentAvailabilitiesWithLength$ = new BehaviorSubject<
    EmploymentAvailabilityWithDaysBetween[]
  >([]);

  availabilityDayWidth$ = new BehaviorSubject<number>(0);

  @ViewChild('availabilityTable') availabilityTable: ElementRef | undefined;
  @HostListener('window:resize', ['$event'])
  calculateDayWidth() {
    if (!this.availabilityTable) {
      return;
    }
    const newWidth =
      this.availabilityTable?.nativeElement.getBoundingClientRect().width /
      this.availabilityDays$.value.length;
    this.availabilityDayWidth$.next(newWidth);
    this.changeDetectorRef.detectChanges();
  }

  constructor(
    private ngZone: NgZone,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  availabilityTracker?: Tracker.Computation;

  async ngOnInit() {
    this.availabilityTracker = Tracker.autorun(() => {
      if (!this.fromDate || !this.toDate) return;
      if (this.user) {
        const daysBetween = utcDifferenceInDays(this.toDate, this.fromDate);

        const newAvailabilityDays: string[] = [];
        for (let i = 0; i < daysBetween + 1; i++) {
          newAvailabilityDays.push(
            format(addDays(this.fromDate, i), dbDayDateToken),
          );
        }
        this.availabilityDays$.next(newAvailabilityDays);
        setTimeout(() => {
          this.calculateDayWidth();
        });

        const newEmploymentAvailabilities = this.user.employmentAvailabilities
          .filter((availability) => {
            let startDate = new Date(availability.startDate);
            let endDate = new Date(availability.endDate);
            return (
              (this.fromDate! <= startDate && this.toDate! >= startDate) ||
              (this.toDate! >= endDate && this.fromDate! <= endDate) ||
              (this.fromDate! >= startDate && this.toDate! <= endDate)
            );
          })
          .map((availability) =>
            this.prepareAvailability(
              availability,
              getUnixTime(this.fromDate!),
              getUnixTime(this.toDate!),
            ),
          );
        this.employmentAvailabilitiesWithLength$.next(
          newEmploymentAvailabilities,
        );
      } else if (this.externalAvailabilities) {
        const daysBetween = utcDifferenceInDays(this.toDate, this.fromDate);

        const newAvailabilityDays: string[] = [];
        for (let i = 0; i < daysBetween + 1; i++) {
          newAvailabilityDays.push(
            format(addDays(this.fromDate, i), dbDayDateToken),
          );
        }
        this.availabilityDays$.next(newAvailabilityDays);
        setTimeout(() => {
          this.calculateDayWidth();
        });

        const newEmploymentAvailabilities = this.externalAvailabilities
          .filter((availability) => {
            let startDate = new Date(availability.startDate);
            let endDate = new Date(availability.endDate);
            return (
              (this.fromDate! <= startDate && this.toDate! >= startDate) ||
              (this.toDate! >= endDate && this.fromDate! <= endDate) ||
              (this.fromDate! >= startDate && this.toDate! <= endDate)
            );
          })
          .map((availability) =>
            this.prepareAvailability(
              availability,
              getUnixTime(this.fromDate!),
              getUnixTime(this.toDate!),
            ),
          );
        this.employmentAvailabilitiesWithLength$.next(
          newEmploymentAvailabilities,
        );
      }
    });
  }

  prepareAvailability(
    availability: EmploymentAvailability,
    calendarStartUnix: number,
    calendarEndUnix: number,
  ): EmploymentAvailabilityWithDaysBetween {
    let newAvailability = { ...availability };
    let startDateUnix = getUnixTime(new Date(availability.startDate));
    let endDateUnix = getUnixTime(new Date(availability.endDate));

    if (startDateUnix < calendarStartUnix && endDateUnix > calendarStartUnix) {
      newAvailability.startDate = format(
        fromUnixTime(calendarStartUnix),
        dbDayDateToken,
      );
      startDateUnix = calendarStartUnix;
    }

    if (endDateUnix > calendarEndUnix && startDateUnix < calendarEndUnix) {
      newAvailability.endDate = format(
        fromUnixTime(calendarEndUnix),
        dbDayDateToken,
      );
      endDateUnix = calendarEndUnix;
    }

    const daysBetween = utcDifferenceInDays(
      fromUnixTime(endDateUnix),
      fromUnixTime(startDateUnix),
    );

    return {
      ...newAvailability,
      daysBetween: daysBetween + 1,
      offset: utcDifferenceInDays(
        fromUnixTime(startDateUnix),
        fromUnixTime(calendarStartUnix),
      ),
    };
  }

  ngOnDestroy() {
    if (this.availabilityTracker) {
      this.availabilityTracker.stop();
    }
  }
}
